/* eslint-disable */


import React, { useEffect, useState } from "react";
import { Boxes, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { sortByName } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

/**
 * Dialog "Inventaire initial" pour un distributeur.
 *
 * Permet de déclarer en masse les barquettes physiquement présentes dans un
 * distributeur au démarrage (jour 1) ou pour ajouter du contenu rétroactif
 * (lots fabriqués les jours précédents non encore saisis).
 *
 * Multi-sessions OK : peut être lancé plusieurs fois, chaque appel cumule.
 */
const emptyLine = () => ({
  product_id: "",
  quantity: 1,
  production_date: "",
  dlc: "",
  lot_number: "",
});

export default function InitialStockDialog({ distributorId, distributorName, onSaved }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [freeSlots, setFreeSlots] = useState(null);
  const [sessionLabel, setSessionLabel] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setLines([emptyLine()]);
      setSessionLabel("");
      return;
    }
    (async () => {
      try {
        const [p, fs] = await Promise.all([
          api.get("/products"),
          api.get(`/distributors/${distributorId}/free-slots`),
        ]);
        const sortedProducts = [...(p.data || [])].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })
        );
        setProducts(sortedProducts);
        setFreeSlots(fs.data);
        // Pré-remplit la session avec la date du jour
        const today = new Date().toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit" });
        setSessionLabel(`Inventaire ${today}`);
      } catch (e) {
        toast.error("Impossible de charger les données du distributeur");
      }
    })();
  }, [open, distributorId]);

  const updateLine = (idx, patch) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  // Préremplit DLC automatiquement quand on saisit la date de fabrication
  const handleProductionDateChange = (idx, value) => {
    const ln = lines[idx];
    const product = products.find((p) => p.id === ln.product_id);
    const patch = { production_date: value };
    if (product?.shelf_life_days && value && !ln.dlc) {
      const d = new Date(value);
      d.setDate(d.getDate() + Number(product.shelf_life_days));
      patch.dlc = d.toISOString().slice(0, 10);
    }
    updateLine(idx, patch);
  };

  const totalToAdd = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0), 0);
  const capacityWarning = freeSlots && totalToAdd > freeSlots.free;

  const submit = async () => {
    // Validation côté client
    for (const [i, ln] of lines.entries()) {
      if (!ln.product_id) return toast.error(`Ligne ${i + 1} : produit manquant`);
      if (!ln.quantity || Number(ln.quantity) < 1) return toast.error(`Ligne ${i + 1} : quantité invalide`);
      if (!ln.production_date) return toast.error(`Ligne ${i + 1} : date de fabrication manquante`);
      if (!ln.dlc) return toast.error(`Ligne ${i + 1} : DLC manquante`);
      if (!ln.lot_number) return toast.error(`Ligne ${i + 1} : n° de lot manquant`);
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/distributors/${distributorId}/initial-stock`, {
        session_label: sessionLabel,
        lines: lines.map((l) => ({
          product_id: l.product_id,
          quantity: Number(l.quantity),
          production_date: l.production_date,
          dlc: l.dlc,
          lot_number: l.lot_number,
        })),
      });
      toast.success(`${data.total_barquettes_added} barquette(s) ajoutée(s)`);
      setOpen(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
        onClick={() => setOpen(true)}
        data-testid="open-initial-stock"
      >
        <Boxes className="w-4 h-4 mr-2" /> Inventaire initial
      </Button>
      <DialogContent className="max-w-3xl" data-testid="initial-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="w-5 h-5 text-[#8C6B2F]" />
            Inventaire initial — {distributorName}
          </DialogTitle>
          <DialogDescription className="text-left">
            Déclarez les barquettes physiquement présentes (lot, DLC, qté). Multi-sessions
            autorisé pour les zones grises (vente nocturne, lots fabriqués hier).
          </DialogDescription>
        </DialogHeader>

        {/* Capacité dispo */}
        {freeSlots && (
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="border border-[#E5E5E0] rounded p-2">
              <div className="text-[10px] uppercase text-[#A3A6A3]">Capacité totale</div>
              <div className="text-base font-mono text-[#1A1C1A]">{freeSlots.total_capacity}</div>
            </div>
            <div className="border border-[#E5E5E0] rounded p-2">
              <div className="text-[10px] uppercase text-[#A3A6A3]">Déjà occupée</div>
              <div className="text-base font-mono text-[#1F4E3D]">{freeSlots.used}</div>
            </div>
            <div className={`border rounded p-2 ${capacityWarning ? "border-[#C05C47] bg-[#FCEEEA]" : "border-[#E5E5E0]"}`}>
              <div className="text-[10px] uppercase text-[#A3A6A3]">Libre</div>
              <div className={`text-base font-mono ${capacityWarning ? "text-[#C05C47]" : "text-[#8C6B2F]"}`}>{freeSlots.free}</div>
            </div>
          </div>
        )}

        {/* Session label */}
        <div>
          <Label className="mb-1 block text-xs">Libellé de la session (optionnel)</Label>
          <Input
            value={sessionLabel}
            onChange={(e) => setSessionLabel(e.target.value)}
            placeholder="Ex: Démarrage 01/06, Rattrapage zone grise…"
            className="h-8 text-sm"
            data-testid="initial-stock-session-label"
          />
        </div>

        {/* Lignes */}
        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
          {lines.map((ln, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 border border-[#E5E5E0] rounded bg-[#FAFAF7]">
              <div className="col-span-4">
                <Label className="text-[10px] mb-0.5 block text-[#575A57]">Produit *</Label>
                <Select value={ln.product_id} onValueChange={(v) => updateLine(idx, { product_id: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`init-line-product-${idx}`}>
                    <SelectValue placeholder="Choisir…" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortByName(products).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1">
                <Label className="text-[10px] mb-0.5 block text-[#575A57]">Qté *</Label>
                <Input
                  type="number" min="1" step="1"
                  value={ln.quantity}
                  onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                  className="h-8 text-xs"
                  data-testid={`init-line-qty-${idx}`}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] mb-0.5 block text-[#575A57]">Date fabrication *</Label>
                <Input
                  type="date"
                  value={ln.production_date}
                  onChange={(e) => handleProductionDateChange(idx, e.target.value)}
                  className="h-8 text-xs"
                  data-testid={`init-line-prod-date-${idx}`}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] mb-0.5 block text-[#575A57]">DLC *</Label>
                <Input
                  type="date"
                  value={ln.dlc}
                  onChange={(e) => updateLine(idx, { dlc: e.target.value })}
                  className="h-8 text-xs"
                  data-testid={`init-line-dlc-${idx}`}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] mb-0.5 block text-[#575A57]">N° Lot *</Label>
                <Input
                  value={ln.lot_number}
                  onChange={(e) => updateLine(idx, { lot_number: e.target.value })}
                  placeholder="Ex: 29526"
                  className="h-8 text-xs font-mono"
                  data-testid={`init-line-lot-${idx}`}
                />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button
                  variant="ghost" size="icon"
                  onClick={() => removeLine(idx)}
                  disabled={lines.length === 1}
                  className="h-8 w-8 text-[#C05C47]"
                  data-testid={`init-line-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Button variant="outline" onClick={addLine} className="text-sm" data-testid="add-init-line">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter une ligne
        </Button>

        {/* Récap total */}
        <div className={`flex items-center justify-between px-3 py-2 rounded border ${
          capacityWarning
            ? "bg-[#FCEEEA] border-[#E8C1B4] text-[#7A3324]"
            : "bg-[#EAF4EE] border-[#C5DBCF] text-[#1F4E3D]"
        }`}>
          <div className="text-sm">
            <span className="font-mono font-bold text-base">{totalToAdd}</span>
            <span className="text-xs ml-1.5">barquette(s) à placer sur {lines.length} ligne(s)</span>
          </div>
          {capacityWarning && (
            <div className="flex items-center gap-1 text-xs">
              <AlertTriangle className="w-3.5 h-3.5" /> Capacité insuffisante
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="initial-stock-cancel">Fermer</Button>
          <Button
            onClick={submit}
            disabled={saving || capacityWarning || totalToAdd === 0}
            className="bg-[#1F4E3D] hover:bg-[#173B2E]"
            data-testid="initial-stock-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
